import { Calendar, Clock, Star } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import { islamicDatesAPI } from "../lib/api";
import { Card, CardContent } from "./ui/card";

const IslamicEventsWidget = () => {
  const { t, lang, isRTL } = useLanguage();
  const [dates, setDates] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDates = async () => {
      try {
        const data = await islamicDatesAPI.get();
        setDates(data);
      } catch (error) {
        console.error("Failed to fetch Islamic dates:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDates();
  }, []);

  const labels = {
    ro: {
      importantDates: "Date importante",
      currentHijriYear: "An Hijri curent",
      eidAlFitr: "Eid al-Fitr",
      eidAlAdha: "Eid al-Adha",
      ramadan: "Ramadan",
      fridayPrayer: "Rugăciunea de vineri",
      eidPrayer: "Rugăciunea de Eid",
      khutba: "Predica (Khutba)",
      prayer: "Rugăciunea",
      daysRemaining: "zile rămase",
      today: "Astăzi",
      ongoing: "În curs",
      passed: "Trecut",
      noEvents: "Niciun eveniment în curând",
    },
    en: {
      importantDates: "Important Dates",
      currentHijriYear: "Current Hijri Year",
      eidAlFitr: "Eid al-Fitr",
      eidAlAdha: "Eid al-Adha",
      ramadan: "Ramadan",
      fridayPrayer: "Friday Prayer",
      eidPrayer: "Eid Prayer",
      khutba: "Sermon (Khutba)",
      prayer: "Prayer",
      daysRemaining: "days remaining",
      today: "Today",
      ongoing: "Ongoing",
      passed: "Passed",
      noEvents: "No upcoming events",
    },
    ar: {
      importantDates: "مواعيد هامة",
      currentHijriYear: "السنة الهجرية الحالية",
      eidAlFitr: "عيد الفطر",
      eidAlAdha: "عيد الأضحى",
      ramadan: "رمضان",
      fridayPrayer: "صلاة الجمعة",
      eidPrayer: "صلاة العيد",
      khutba: "الخطبة",
      prayer: "الصلاة",
      daysRemaining: "أيام متبقية",
      today: "اليوم",
      ongoing: "جاري",
      passed: "انتهى",
      noEvents: "لا توجد أحداث قادمة",
    },
  };

  const l = labels[lang] || labels.ro;

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eventDate = new Date(dateStr);
    eventDate.setHours(0, 0, 0, 0);
    const diff = Math.ceil((eventDate - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const isOngoing = (startStr, endStr) => {
    if (!startStr || !endStr) return false;
    const today = new Date();
    const start = new Date(startStr);
    const end = new Date(endStr);
    return today >= start && today <= end;
  };

  const formatDateRange = (startStr, endStr) => {
    if (!startStr) return "";
    const start = new Date(startStr);
    const options = { day: "numeric", month: "short" };
    const locale = lang === "ar" ? "ar-SA" : lang === "en" ? "en-GB" : "ro-RO";

    if (endStr) {
      const end = new Date(endStr);
      return `${start.toLocaleDateString(
        locale,
        options,
      )} - ${end.toLocaleDateString(locale, options)}`;
    }
    return start.toLocaleDateString(locale, options);
  };

  if (loading) {
    return (
      <Card className="bg-white border-stone-200 shadow-lg">
        <CardContent className="p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-stone-200 rounded w-1/2 mb-4"></div>
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-stone-200 rounded"></div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Get prayer times from dates or use defaults
  const specialPrayers = {
    friday: {
      khutba: dates?.friday_khutba || "13:15",
      prayer: dates?.friday_prayer || "13:45",
      khutbaVisible:
        dates?.show_friday_prayer !== false &&
        dates?.show_friday_khutba !== false,
      prayerVisible:
        dates?.show_friday_prayer !== false &&
        dates?.show_friday_prayer_time !== false,
    },
    eidFitr: {
      prayer: dates?.eid_fitr_prayer || "08:00",
      prayerVisible: dates?.show_eid_fitr_prayer !== false,
    },
    eidAdha: {
      prayer: dates?.eid_adha_prayer || "08:00",
      prayerVisible: dates?.show_eid_adha_prayer !== false,
    },
  };

  const events = dates
    ? [
        {
          id: "ramadan",
          name: l.ramadan,
          startDate: dates.ramadan_start,
          endDate: dates.ramadan_end,
          icon: "🌙",
          color: "bg-purple-100 text-purple-700",
          isVisible: dates?.show_ramadan !== false,
        },
        {
          id: "eidFitr",
          name: l.eidAlFitr,
          startDate: dates.eid_al_fitr_start,
          endDate: dates.eid_al_fitr_end,
          icon: "🎉",
          color: "bg-teal-100 text-teal-700",
          prayerTime: specialPrayers.eidFitr.prayerVisible
            ? specialPrayers.eidFitr.prayer
            : "",
          isVisible: dates?.show_eid_al_fitr !== false,
        },
        {
          id: "eidAdha",
          name: l.eidAlAdha,
          startDate: dates.eid_al_adha_start,
          endDate: dates.eid_al_adha_end,
          icon: "🐑",
          color: "bg-amber-100 text-amber-700",
          prayerTime: specialPrayers.eidAdha.prayerVisible
            ? specialPrayers.eidAdha.prayer
            : "",
          isVisible: dates?.show_eid_al_adha !== false,
        },
      ].filter((event) => event.isVisible && !!event.startDate)
    : [];

  return (
    <Card
      className="bg-white border-stone-200 shadow-lg"
      data-testid="islamic-events-widget">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg font-semibold text-stone-800 flex items-center gap-2">
            <Star className="w-5 h-5 text-islamic-gold" />
            {l.importantDates}
          </h3>
        </div>

        {/* Friday Prayer Times */}
        {(specialPrayers.friday.khutbaVisible ||
          specialPrayers.friday.prayerVisible) && (
          <div className="mb-4 p-4 bg-islamic-teal/5 rounded-lg border border-islamic-teal/20">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-islamic-teal" />
              <span className="font-medium text-islamic-teal">
                {l.fridayPrayer}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {specialPrayers.friday.khutbaVisible && (
                <div>
                  <span className="text-stone-500">{l.khutba}:</span>
                  <span className="font-mono font-medium text-stone-800 ml-2">
                    {specialPrayers.friday.khutba}
                  </span>
                </div>
              )}
              {specialPrayers.friday.prayerVisible && (
                <div>
                  <span className="text-stone-500">{l.prayer}:</span>
                  <span className="font-mono font-medium text-stone-800 ml-2">
                    {specialPrayers.friday.prayer}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {dates?.show_hijri_year !== false && dates?.hijri_year && (
          <div className="mb-4 p-3 bg-stone-50 rounded-lg border border-stone-200">
            <div className="text-sm text-stone-500">{l.currentHijriYear}</div>
            <div className="font-semibold text-stone-800">
              {dates.hijri_year}
            </div>
          </div>
        )}

        {/* Events list */}
        {events.length > 0 ? (
          <div className="space-y-3">
            {events.map((event) => {
              const days = getDaysUntil(event.startDate);
              const ongoing = isOngoing(event.startDate, event.endDate);

              return (
                <div
                  key={event.id}
                  className={`p-4 rounded-lg border ${
                    ongoing
                      ? "border-islamic-gold bg-islamic-gold/5"
                      : "border-stone-200 bg-stone-50"
                  }`}
                  data-testid={`event-${event.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{event.icon}</span>
                      <div>
                        <h4 className="font-semibold text-stone-800">
                          {event.name}
                        </h4>
                        <p className="text-sm text-stone-500">
                          {formatDateRange(event.startDate, event.endDate)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {ongoing ? (
                        <span className="px-2 py-1 bg-islamic-gold text-white text-xs rounded-full">
                          {l.ongoing}
                        </span>
                      ) : days === 0 ? (
                        <span className="px-2 py-1 bg-islamic-teal text-white text-xs rounded-full">
                          {l.today}
                        </span>
                      ) : days > 0 ? (
                        <span className="text-sm text-stone-600">
                          {days} {l.daysRemaining}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  {/* Eid prayer time */}
                  {event.prayerTime && (
                    <div className="mt-3 pt-3 border-t border-stone-200">
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-stone-400" />
                        <span className="text-stone-500">{l.eidPrayer}:</span>
                        <span className="font-mono font-medium text-stone-800">
                          {event.prayerTime}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-center text-stone-500 py-4">{l.noEvents}</p>
        )}
      </CardContent>
    </Card>
  );
};

export default IslamicEventsWidget;
